import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  // Initialize cart from localStorage
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem('bakery_cart');
    return savedCart ? JSON.parse(savedCart) : { items: [], subtotal: 0 };
  });

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('bakery_cart', JSON.stringify(cart));
  }, [cart]);

  const calculateSubtotal = (items) => {
    return items.reduce((sum, item) => sum + item.subtotal, 0);
  };

  const addCake = (cakeData) => {
    const {
      cakeId,
      cakeTitle,
      cakeImage,
      weight,
      flavourId,
      flavourName,
      flavourPrice,
      basePrice,
      baseWeight,
      customDesignFee,
      quantity = 1
    } = cakeData;

    // Calculate cake price: base price for base weight, 75% increase per 500gm above base
    const selectedWeight = parseFloat(weight);
    const baseWeightValue = parseFloat(baseWeight || 1);
    const basePriceValue = parseFloat(basePrice);
    
    let cakePrice = basePriceValue; // Start with base price
    
    // Only apply increment if weight is more than base weight
    if (selectedWeight > baseWeightValue) {
      const additionalWeight = selectedWeight - baseWeightValue;
      const pricePerHalfKg = basePriceValue / (baseWeightValue / 0.5);
      const additionalHalfKgs = additionalWeight / 0.5;
      const baseHalfKgs = baseWeightValue / 0.5;
      
      for (let i = 0; i < additionalHalfKgs; i++) {
        cakePrice += pricePerHalfKg * Math.pow(1.75, baseHalfKgs + i) / Math.pow(1.75, baseHalfKgs - 1);
      }
    } else if (selectedWeight < baseWeightValue) {
      cakePrice = basePriceValue * (selectedWeight / baseWeightValue);
    }
    
    const unitPrice = cakePrice + parseFloat(customDesignFee || 0) + 
                     (parseFloat(flavourPrice) * (selectedWeight / 0.5));
    const subtotal = unitPrice * quantity;

    setCart(prevCart => {
      const existingIndex = prevCart.items.findIndex(item =>
        item.type === 'cake' &&
        item.cakeId === cakeId &&
        item.weight === weight &&
        item.flavourId === flavourId
      );

      let newItems;
      if (existingIndex > -1) {
        newItems = [...prevCart.items];
        newItems[existingIndex].quantity += quantity;
        newItems[existingIndex].subtotal = newItems[existingIndex].unitPrice * newItems[existingIndex].quantity;
      } else {
        newItems = [
          ...prevCart.items,
          {
            id: Date.now() + Math.random(),
            type: 'cake',
            cakeId,
            cakeTitle,
            cakeImage,
            weight,
            flavourId,
            flavourName,
            flavourPrice,
            basePrice,
            baseWeight,
            customDesignFee,
            quantity,
            unitPrice: Math.round(unitPrice),
            subtotal: Math.round(subtotal)
          }
        ];
      }

      return {
        items: newItems,
        subtotal: calculateSubtotal(newItems)
      };
    });
  };

  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity < 1) {
      removeItem(itemId);
      return;
    }

    setCart(prevCart => {
      const newItems = prevCart.items.map(item => {
        if (item.id === itemId) {
          return {
            ...item,
            quantity: newQuantity,
            subtotal: item.unitPrice * newQuantity
          };
        }
        return item;
      });

      return {
        items: newItems,
        subtotal: calculateSubtotal(newItems)
      };
    });
  };

  const updateWeight = (itemId, newWeight) => {
    setCart(prevCart => {
      const newItems = prevCart.items.map(item => {
        if (item.id === itemId && item.type === 'cake') {
          // Recalculate unit price with new weight
          const selectedWeight = parseFloat(newWeight);
          const baseWeightValue = parseFloat(item.baseWeight || 1);
          const basePriceValue = parseFloat(item.basePrice);
          
          let cakePrice = basePriceValue;
          
          if (selectedWeight > baseWeightValue) {
            const additionalWeight = selectedWeight - baseWeightValue;
            const pricePerHalfKg = basePriceValue / (baseWeightValue / 0.5);
            const additionalHalfKgs = additionalWeight / 0.5;
            const baseHalfKgs = baseWeightValue / 0.5;
            
            for (let i = 0; i < additionalHalfKgs; i++) {
              cakePrice += pricePerHalfKg * Math.pow(1.75, baseHalfKgs + i) / Math.pow(1.75, baseHalfKgs - 1);
            }
          } else if (selectedWeight < baseWeightValue) {
            cakePrice = basePriceValue * (selectedWeight / baseWeightValue);
          }
          
          const unitPrice = cakePrice + parseFloat(item.customDesignFee || 0) + 
                           (parseFloat(item.flavourPrice) * (selectedWeight / 0.5));
          const subtotal = unitPrice * item.quantity;
          
          return {
            ...item,
            weight: newWeight,
            unitPrice: Math.round(unitPrice),
            subtotal: Math.round(subtotal)
          };
        }
        return item;
      });

      return {
        items: newItems,
        subtotal: calculateSubtotal(newItems)
      };
    });
  };

  const removeItem = (itemId) => {
    setCart(prevCart => {
      const newItems = prevCart.items.filter(item => item.id !== itemId);
      return {
        items: newItems,
        subtotal: calculateSubtotal(newItems)
      };
    });
  };

  const clearCart = () => {
    setCart({ items: [], subtotal: 0 });
  };

  const getTotals = () => {
    const subtotal = cart.subtotal;
    const tax = subtotal * 0.05; // 5% tax
    const deliveryFee = 50;
    const total = subtotal + tax + deliveryFee;

    return {
      subtotal: Math.round(subtotal),
      tax: Math.round(tax * 100) / 100,
      deliveryFee,
      total: Math.round(total * 100) / 100
    };
  };

  const getItemCount = () => {
    return cart.items.length;
  };

  const value = {
    cart,
    addCake,
    updateQuantity,
    updateWeight,
    removeItem,
    clearCart,
    getTotals,
    getItemCount
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
